import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { jwtDecode } from 'jwt-decode';
import { Button, Form, Input, message, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import useAuthContext from '../../contexts/AuthContext';
import usePwdPattern from '../../utils/pwdPattern';
import { outOfNavRoutes } from '../../utils/constants/routes';
import useErrorMessage from '../../utils/ErrorMessage';

const ChangePwdForm = ({ token, switchForm }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { dispatchAPI } = useAuthContext();
  const { message: customMessage } = useErrorMessage();
  const [isLoading, setLoading] = useState(false);
  const [isTokenValid, setIsTokenValid] = useState(true);
  const pwdPattern = usePwdPattern();

  const changePwd = async (body) => {
    setLoading(true);
    try {
      await dispatchAPI('POST', { url: `reset-pwd/${token}`, body });
      message.success(t('login.passwordChanged'));
      switchForm('login');
      navigate(outOfNavRoutes.LOGIN);
    } catch (e) {
      if (e.response && e.response.data && e.response.data.description) {
        message.error(e.response.data.description);
      } else {
        message.error(t('login.wrongToken'));
      }
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    await changePwd(values);
  };

  useEffect(() => {
    try {
      const decoded = jwtDecode(token);
      const currentTime = Date.now() / 1000;
      if (decoded.exp < currentTime) {
        setIsTokenValid(false);
      } else setIsTokenValid(true);
    } catch (e) {
      setIsTokenValid(false);
    }
  }, [token]);

  const handleResendLink = async () => {
    setLoading(true);
    try {
      await dispatchAPI('GET', { url: `/reset-pwd/resend/${token}` });
      message.success(
        'Un mail avec un nouveau lien vient de vous être envoyé.'
      );
    } catch (e) {
      customMessage(e);
    }
    setLoading(false);
  };

  return !isTokenValid ? (
    <>
      <Typography.Title
        level={5}
        style={{
          color: 'var(--componentBackground)'
        }}
      >
        Il semble que votre lien est expiré. Cliquez sur le boutton pour
        recevoir un nouveau lien
      </Typography.Title>
      <Button type="primary" onClick={handleResendLink} loading={isLoading}>
        Renvoyer le lien
      </Button>
    </>
  ) : (
    <Form onFinish={handleSubmit} className="login-form">
      <Form.Item
        name="password"
        rules={[
          { required: true, message: t('login.pwdMissing') },
          ...pwdPattern
        ]}
        hasFeedback
      >
        <Input.Password
          prefix={
            <LockOutlined style={{ color: 'var(--textColorSecondary)' }} />
          }
          placeholder={t('login.password')}
        />
      </Form.Item>
      <Form.Item
        dependencies={['password']}
        hasFeedback
        name="confirm"
        rules={[
          {
            required: true,
            message: t('login.pwdMissing')
          },
          ({ getFieldValue }) => ({
            validator(_, value) {
              if (!value || getFieldValue('password') === value) {
                return Promise.resolve();
              }
              return Promise.reject(t('login.pwdNotMatching'));
            }
          })
        ]}
      >
        <Input.Password
          prefix={<LockOutlined style={{ color: 'rgba(0,0,0,.25)' }} />}
          placeholder={t('login.confirmPassword')}
        />
      </Form.Item>
      <Form.Item>
        <Button
          type="primary"
          htmlType="submit"
          style={{ width: '100%' }}
          loading={isLoading}
        >
          {t('login.resetPwd')}
        </Button>
      </Form.Item>
    </Form>
  );
};

ChangePwdForm.propTypes = {
  token: PropTypes.string,
  switchForm: PropTypes.func.isRequired
};

ChangePwdForm.defaultProps = {
  token: null
};

export default ChangePwdForm;
